import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const AddClient = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Form States
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        gstin: "",
        address: "",
        state_code: ""
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Error clear panna
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic Frontend Validation for Phone (matching your PHP regex)
        if (!/^[0-9]{10}$/.test(formData.phone)) {
            return toast.error("Please enter a valid 10-digit phone number");
        }

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/clients/create.php",
                data: formData
            });

            if (res?.success) {
                Swal.fire("Success!", "Client registered successfully", "success");
                navigate("/clients"); // Unnoda client list path-ku poidu machan
                dispatch(loadingFalse());
            } else {
                if (res?.errors) {
                    setErrors(res.errors);
                } else {
                    toast.error(res?.message || "Failed to create client");
                }
                dispatch(loadingFalse());
            }
        } catch (err) {
            Swal.fire("Error", "Server error occurred", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Management &gt; Clients</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold">Register New Client</h3>
                        <p className="text-muted">Add B2B clients or merchants for cotton sales.</p>
                    </div>
                    <Button color="secondary" outline onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back"></i> Back
                    </Button>
                </div>
            </div>

            <Form onSubmit={handleSubmit}>
                <Row>
                    {/* Left Side: Basic Info */}
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4">Client Information</h5>
                                <Row>
                                    <Col md={12} className="mb-3">
                                        <FormGroup>
                                            <Label className="fw-bold">Company / Client Name <span className="text-danger">*</span></Label>
                                            <Input
                                                name="name"
                                                placeholder="Enter full name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={errors.name ? "is-invalid" : ""}
                                                required
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="fw-bold">Phone Number <span className="text-danger">*</span></Label>
                                            <InputGroup>
                                                <InputGroupText><i className="bx bx-phone"></i></InputGroupText>
                                                <Input
                                                    type="number"
                                                    name="phone"
                                                    placeholder="10 digit mobile"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    className={errors.phone ? "is-invalid" : ""}
                                                    required
                                                />
                                            </InputGroup>
                                            {errors.phone && <div className="text-danger small mt-1">{errors.phone}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="fw-bold">Email Address</Label>
                                            <InputGroup>
                                                <InputGroupText><i className="bx bx-envelope"></i></InputGroupText>
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    placeholder="client@example.com"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    className={errors.email ? "is-invalid" : ""}
                                                />
                                            </InputGroup>
                                            {errors.email && <div className="text-danger small mt-1">{errors.email}</div>}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <FormGroup className="mt-3">
                                    <Label className="fw-bold">Full Address</Label>
                                    <Input
                                        type="textarea"
                                        name="address"
                                        rows="3"
                                        placeholder="Enter billing address"
                                        value={formData.address}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Right Side: Statutory Info (GST) */}
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4">GST Details</h5>
                                <FormGroup>
                                    <Label className="fw-bold">GSTIN</Label>
                                    <Input
                                        name="gstin"
                                        placeholder="33XXXXX0000X1Z1"
                                        style={{ textTransform: 'uppercase' }}
                                        value={formData.gstin}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                                <FormGroup className="mt-3">
                                    <Label className="fw-bold">State Code</Label>
                                    <Input
                                        type="number"
                                        name="state_code"
                                        placeholder="e.g. 33 for TN"
                                        value={formData.state_code}
                                        onChange={handleChange}
                                    />
                                </FormGroup>

                                <div className="bg-light p-3 mt-4" style={{ borderRadius: '10px', borderLeft: '4px solid #2e4a3d' }}>
                                    <small className="text-muted">
                                        <i className="bx bx-info-circle me-1"></i>
                                        GSTIN and State Code are optional but recommended for B2B billing.
                                    </small>
                                </div>
                            </CardBody>
                        </Card>

                        <Button type="submit" className="add-btn w-100 py-3 fw-bold shadow-sm">
                            <i className="bx bx-user-plus me-1"></i> REGISTER CLIENT
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default AddClient;